import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { categories, allComics } from "@/mocks/comics";
import type { Comic } from "@/mocks/comics";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || "all");
  const [sortBy, setSortBy] = useState<"latest" | "popular" | "rating">("latest");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useEffect(() => {
    setActiveSlug(slug || "all");
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = activeSlug === "all" ? "全部漫画" : currentCategory?.name || "全部漫画";

  let filtered: Comic[] =
    activeSlug === "all"
      ? [...allComics]
      : allComics.filter((c) => c.category === activeSlug);

  if (sortBy === "popular") {
    filtered.sort((a, b) => b.views - a.views);
  } else if (sortBy === "rating") {
    filtered.sort((a, b) => b.rating - a.rating);
  } else {
    filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hanmanwu.com/" },
      { "@type": "ListItem", position: 2, name: categoryName, item: `https://hanmanwu.com/category/${activeSlug}` },
    ],
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: filtered.slice(0, 20).map((comic, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "ComicSeries",
        name: comic.title,
        url: `https://hanmanwu.com/comic/${comic.id}`,
        author: { "@type": "Person", name: comic.author },
        description: comic.description,
      },
    })),
  };

  return (
    <>
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={itemListSchema} />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 py-4 text-xs text-foreground-500 font-label flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{categoryName}</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filtered.length} 部漫画
              {currentCategory && ` - 精选${currentCategory.name}类韩漫，免费在线阅读`}
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="lg:w-48 flex-shrink-0">
              <div className="lg:sticky lg:top-20">
                <h4 className="text-sm font-semibold font-label text-foreground-800 mb-3">漫画分类</h4>
                <ul className="flex lg:flex-col flex-wrap gap-1">
                  <li>
                    <Link
                      to="/category/all"
                      className={`block px-3 py-2 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                        activeSlug === "all"
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:bg-background-100"
                      }`}
                    >
                      全部
                    </Link>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`block px-3 py-2 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                          activeSlug === cat.slug
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                  {(["latest", "popular", "rating"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setSortBy(s)}
                      className={`px-4 py-1.5 rounded-full text-xs font-label transition-all whitespace-nowrap cursor-pointer ${
                        sortBy === s
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {s === "latest" ? "最新" : s === "popular" ? "人气" : "评分"}
                    </button>
                  ))}
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                  <p className="mt-3 text-foreground-500">暂无该分类的漫画</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                  {filtered.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/comic/${comic.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2.5">
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <div className="absolute top-2 left-2">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-label ${
                              comic.status === "连载"
                                ? "bg-accent-500 text-background-50"
                                : "bg-foreground-600 text-background-50"
                            }`}
                          >
                            {comic.status}
                          </span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-background-50/90 font-label">
                              共{comic.chapters}话
                            </span>
                            <span className="text-[10px] text-accent-300 font-label">
                              <i className="ri-star-fill text-[10px]"></i> {comic.rating}
                            </span>
                          </div>
                        </div>
                      </div>
                      <h3 className="text-sm font-medium font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5 font-label">
                        {comic.author}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50 py-10 md:py-14 mt-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-3">
              韩漫网站免费在线阅读 - {categoryName}
            </h2>
            <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              韩漫屋为您提供{categoryName}韩漫免费在线阅读服务。无删减漫画内容完整呈现，下拉式界面让阅读更加流畅舒适。访问专属漫画免费韩漫网址，即刻开启韩国漫画阅读之旅。
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}